/**
 * SPJ-1724: popup del badge forecast. Mostra i prodotti (e i negozi) che il modello ha usato
 * per prevedere lo SKU: sono i "vicini" salvati dal predict-all in forecast_similarity, con
 * immagine, descrizione e colore del prodotto. Per ogni vicino, nel suo negozio (SPJ-1863): i
 * pezzi venduti usati dal modello (salvati dal forecast; per i forecast precedenti ricostruiti
 * dalla stagione usata) e l'ordinato di quella stagione.
 *
 * Stesso schema del pannello ordini: portale su document.body, ancorato al badge cliccato,
 * si chiude con Esc o cliccando fuori.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { computeTooltipPosition } from './SkuOrderForecastBadges';
import { buildImage, NO_IMAGE_PLACEHOLDER } from '../utils/product';

export type ForecastSimilarProduct = {
  base_sku: string;
  sku?: string | null;
  shop_id?: string | null;
  cod_stagione?: number | null;
  short_descr?: string | null;
  des_modello?: string | null;
  colore?: string | null;
  tipo_filato?: string | null;
  url_img_list?: string | null;
  sketch_url?: string | null;
  ordinato?: number | null;
  venduto?: number | null;
  stagione_usata?: number | null;
  venduto_dal_forecast?: boolean;
};

export type ForecastSimilarProductsResponse = {
  sku: string;
  forecast_sku?: string | null;
  season_id: number;
  shops: Array<{ shop_id: string; products: ForecastSimilarProduct[] }>;
};

export type LoadForecastProducts = (sku: string, shopIds: string[]) => Promise<ForecastSimilarProductsResponse>;

export const FORECAST_PANEL_WIDTH = 420;
export const FORECAST_PANEL_MAX_HEIGHT = 560;

// Una richiesta per (SKU, negozi) per sessione: i vicini cambiano solo a ogni predict-all.
const cache = new Map<string, Promise<ForecastSimilarProductsResponse>>();

function loadCached(sku: string, shopIds: string[], load: LoadForecastProducts) {
  const key = `${sku}|${[...shopIds].sort().join(',')}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = load(sku, shopIds).catch((err) => {
      cache.delete(key);
      throw err;
    });
    cache.set(key, pending);
  }
  return pending;
}

/** Un valore di URL_IMG_LIST / SKETCH_URL e' un id Cloudinary (es. "v1673609375/nsd/232/M0091ML206_10.jpg"),
 * non un URL: va passato a buildImage come nel resto dell'app. Un URL completo resta com'e'. */
function toImageUrl(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//.test(trimmed) ? trimmed : buildImage(trimmed) ?? undefined;
}

/** Prima immagine utile: la prima foto di URL_IMG_LIST ("{id1,id2,...}"), altrimenti lo sketch. */
function firstImage(product: ForecastSimilarProduct): string | undefined {
  const firstPhoto = (product.url_img_list || '').replace(/[{}"]/g, '').split(',')[0];
  return toImageUrl(firstPhoto) ?? toImageUrl(product.sketch_url);
}

export function ForecastProductsPanel({
  anchorRect,
  sku,
  shopIds,
  forecast,
  load,
  onClose,
}: {
  anchorRect: DOMRect;
  sku: string;
  shopIds: string[];
  forecast?: number;
  load: LoadForecastProducts;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() =>
    computeTooltipPosition(anchorRect, FORECAST_PANEL_WIDTH, FORECAST_PANEL_MAX_HEIGHT),
  );
  const [data, setData] = useState<ForecastSimilarProductsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const reposition = () =>
      setPos(computeTooltipPosition(anchorRect, FORECAST_PANEL_WIDTH, el.getBoundingClientRect().height));
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    window.addEventListener('resize', reposition);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [anchorRect]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadCached(sku, shopIds, load)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError('Impossibile caricare i prodotti usati per il forecast.');
      });
    return () => {
      cancelled = true;
    };
  }, [sku, shopIds, load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const shops = data?.shops ?? [];
  const totalProducts = shops.reduce((sum, shop) => sum + shop.products.length, 0);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Prodotti usati per il forecast di ${sku}`}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 999999,
        width: FORECAST_PANEL_WIDTH,
        maxHeight: FORECAST_PANEL_MAX_HEIGHT,
        overflowY: 'auto',
        borderRadius: 8,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        backgroundColor: 'white',
        padding: 16,
        border: '2px solid #A5838C',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Prodotti usati per il forecast</div>
          <div className="font-mono text-sm font-semibold text-gray-900">{sku}</div>
          {forecast !== undefined && (
            <div className="text-xs text-gray-600">Forecast: <span className="font-semibold tabular-nums">{forecast}</span> pezzi</div>
          )}
          {data?.forecast_sku && data.forecast_sku !== data.sku && (
            <div className="text-[11px] text-gray-500">Forecast calcolato sullo SKU origine {data.forecast_sku}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Chiudi"
        >
          ✕
        </button>
      </div>

      {!data && !error && <div className="mt-3 text-sm text-gray-500">Caricamento…</div>}
      {error && <div className="mt-3 text-sm text-red-700">{error}</div>}
      {data && totalProducts === 0 && (
        <div className="mt-3 text-sm text-gray-600">
          Per questo SKU non sono salvati i prodotti usati dal modello (forecast calcolato prima che venissero salvati, o SKU senza forecast in questo negozio).
        </div>
      )}

      {shops.filter((shop) => shop.products.length > 0).map((shop) => (
        <div key={shop.shop_id} className="mt-3">
          {shops.length > 1 && (
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Negozio {shop.shop_id}</div>
          )}
          <ul className="flex flex-col gap-2">
            {shop.products.map((product, index) => {
              const image = firstImage(product);
              return (
                <li key={`${shop.shop_id}-${product.base_sku}-${product.shop_id}-${index}`} className="flex gap-3 rounded border border-gray-200 p-2">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded bg-gray-50">
                    {image ? (
                      <img
                        src={image}
                        alt=""
                        className="h-full w-full object-contain"
                        loading="lazy"
                        onError={event => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = NO_IMAGE_PLACEHOLDER;
                        }}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">n/d</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs font-semibold text-gray-900">{product.base_sku}</div>
                    {product.short_descr && <div className="truncate text-xs text-gray-700" title={product.short_descr}>{product.short_descr}</div>}
                    <div className="text-[11px] text-gray-500">
                      {[product.stagione_usata ? `Stagione ${product.stagione_usata}` : null, product.colore, product.tipo_filato]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-3 text-[11px] text-gray-700">
                      <span>Negozio <span className="font-semibold">{product.shop_id || '—'}</span></span>
                      <span>Ordinato <span className="font-semibold tabular-nums">{product.ordinato ?? 0}</span></span>
                      <span
                        title={
                          product.venduto_dal_forecast
                            ? 'Pezzi venduti usati dal modello per il forecast'
                            : 'Ricostruito dalla stagione usata: questo forecast è precedente al salvataggio del venduto'
                        }
                      >
                        Venduto{' '}
                        <span className="font-semibold tabular-nums">
                          {product.venduto ?? 0}
                        </span>
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
